import * as THREE from "three";

function mulberry(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s += 0x6d2b79f5;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function windowTexture(rng: () => number): THREE.CanvasTexture {
  const c = document.createElement("canvas");
  c.width = 64;
  c.height = 256;
  const ctx = c.getContext("2d")!;
  ctx.fillStyle = "#07080d";
  ctx.fillRect(0, 0, 64, 256);
  for (let y = 4; y < 250; y += 7) {
    for (let x = 4; x < 60; x += 6) {
      if (rng() < 0.38) {
        const pick = rng();
        ctx.fillStyle = pick < 0.55 ? "#3df0ff" : pick < 0.8 ? "#ff2d95" : "#ffb347";
        ctx.globalAlpha = 0.35 + rng() * 0.65;
        ctx.fillRect(x, y, 3, 4);
      }
    }
  }
  ctx.globalAlpha = 1;
  const tex = new THREE.CanvasTexture(c);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

function neonEdges(geometry: THREE.BufferGeometry, color: number): THREE.LineSegments {
  return new THREE.LineSegments(
    new THREE.EdgesGeometry(geometry, 18),
    new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.85 }),
  );
}

export class World {
  readonly root = new THREE.Group();
  private rain: THREE.Points;
  private rainVel: Float32Array;
  private gridMat: THREE.ShaderMaterial;
  private spinners: THREE.Group[] = [];
  private buildings: THREE.Group[] = [];
  private billboardL: THREE.Mesh;
  private holoCtx: CanvasRenderingContext2D;
  private holoTex: THREE.CanvasTexture;

  constructor(scene: THREE.Scene) {
    scene.add(this.root);
    const rng = mulberry(0xb1ade5);

    this.root.add(new THREE.AmbientLight(0x1a2233, 1.4));
    const cyan = new THREE.PointLight(0x3df0ff, 4.5, 80, 1.6);
    cyan.position.set(-8, 14, 8);
    const magenta = new THREE.PointLight(0xff2d95, 3.2, 70, 1.8);
    magenta.position.set(10, 6, -6);
    const amber = new THREE.PointLight(0xffb347, 2.2, 50, 2);
    amber.position.set(0, 8, 16);
    this.root.add(cyan, magenta, amber);

    this.gridMat = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      uniforms: {
        uTime: { value: 0 },
        uDrive: { value: 0.45 },
        uColor: { value: new THREE.Color(0x3df0ff) },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        varying vec2 vUv;
        uniform float uTime;
        uniform float uDrive;
        uniform vec3 uColor;
        void main() {
          vec2 uv = vUv * vec2(42.0, 90.0);
          uv.y -= uTime * (1.8 + uDrive * 6.0);
          vec2 gv = abs(fract(uv) - 0.5);
          float line = 1.0 - smoothstep(0.02, 0.046, min(gv.x, gv.y));
          float fade = smoothstep(0.0, 0.18, vUv.y) * (1.0 - smoothstep(0.72, 1.0, vUv.y));
          float pulse = 0.55 + 0.45 * sin(uTime * 1.6 + vUv.y * 14.0);
          float glow = line * fade * pulse;
          gl_FragColor = vec4(uColor * glow, glow * 0.85);
        }
      `,
    });
    const grid = new THREE.Mesh(new THREE.PlaneGeometry(90, 140), this.gridMat);
    grid.rotation.x = -Math.PI / 2;
    grid.position.z = -28;
    this.root.add(grid);

    this.addCity(rng);
    this.addPyramid();
    this.root.add(this.makeStars());
    const rainPack = this.makeRain();
    this.rain = rainPack.points;
    this.rainVel = rainPack.vel;
    this.root.add(this.rain);
    this.addSpinners();
    const boards = this.addBillboards();
    this.billboardL = boards.left;
    this.holoCtx = boards.ctx;
    this.holoTex = boards.tex;
    this.setQuestion(7, 8);
  }

  setQuestion(a: number, b: number): void {
    const ctx = this.holoCtx;
    ctx.fillStyle = "#05060a";
    ctx.fillRect(0, 0, 512, 256);
    ctx.strokeStyle = "#3df0ff";
    ctx.lineWidth = 6;
    ctx.strokeRect(10, 10, 492, 236);
    ctx.fillStyle = "#ff2d95";
    ctx.font = "600 22px 'Share Tech Mono', monospace";
    ctx.fillText("MATHBLASTER  /  LIVE PRODUCT", 28, 52);
    ctx.fillStyle = "#e7f7ff";
    ctx.font = "800 96px Orbitron, sans-serif";
    ctx.fillText(`${a} × ${b}`, 40, 160);
    ctx.fillStyle = "#ffb347";
    ctx.font = "500 20px 'Share Tech Mono', monospace";
    ctx.fillText("MEMORIZE  ·  DO NOT PAUSE  ·  HOLD THE GRID", 28, 216);
    this.holoTex.needsUpdate = true;
  }

  update(dt: number, time: number, drive = 0.45): void {
    this.gridMat.uniforms.uTime.value = time;
    this.gridMat.uniforms.uDrive.value = drive;
    const pos = this.rain.geometry.getAttribute("position") as THREE.BufferAttribute;
    for (let i = 0; i < this.rainVel.length; i++) {
      pos.setY(i, pos.getY(i) - this.rainVel[i] * dt * (0.8 + drive));
      pos.setZ(i, pos.getZ(i) + 6 * dt);
      if (pos.getY(i) < 0) {
        pos.setY(i, 26);
        pos.setZ(i, (Math.random() - 0.5) * 80);
      }
    }
    pos.needsUpdate = true;
    this.spinners.forEach((s, i) => {
      s.position.x += Math.sin(time * 0.3 + i) * dt * 1.4;
      s.position.z += dt * (4 + i) * (0.6 + drive);
      if (s.position.z > 20) s.position.z = -70;
    });
    for (const b of this.buildings) {
      b.position.z += (5 + drive * 7) * dt;
      if (b.position.z > 22) {
        b.position.z -= 86;
        b.position.x = Math.sign(b.position.x || 1) * (14 + Math.random() * 18);
      }
    }
    const flicker = 0.74 + Math.sin(time * 9) * 0.08;
    (this.billboardL.material as THREE.MeshBasicMaterial).opacity = flicker;
  }

  private addCity(rng: () => number): void {
    const tex = windowTexture(rng);
    const body = new THREE.MeshBasicMaterial({
      map: tex,
      color: 0x9aa8c0,
      transparent: true,
      opacity: 0.92,
    });
    for (let i = 0; i < 72; i++) {
      const side = i % 2 === 0 ? -1 : 1;
      const h = 4 + rng() * 22;
      const w = 1.4 + rng() * 2.6;
      const d = 1.4 + rng() * 3.2;
      const geo = new THREE.BoxGeometry(w, h, d);
      const mesh = new THREE.Mesh(geo, body);
      mesh.position.y = h / 2;
      const edges = neonEdges(geo, rng() > 0.5 ? 0x3df0ff : 0xff2d95);
      edges.position.y = h / 2;
      const g = new THREE.Group();
      g.add(mesh, edges);
      g.position.set(side * (14 + rng() * 18), 0, -8 - rng() * 70);
      this.buildings.push(g);
      this.root.add(g);
    }
  }

  private addPyramid(): void {
    const geo = new THREE.ConeGeometry(16, 18, 4);
    const fill = new THREE.Mesh(
      geo,
      new THREE.MeshBasicMaterial({ color: 0x0a0d16, transparent: true, opacity: 0.7 }),
    );
    const edges = neonEdges(geo, 0xff2d95);
    const g = new THREE.Group();
    g.add(fill, edges);
    g.position.set(0, 8, -78);
    g.rotation.y = Math.PI / 4;
    this.root.add(g);
  }

  private addBillboards(): {
    left: THREE.Mesh;
    right: THREE.Mesh;
    ctx: CanvasRenderingContext2D;
    tex: THREE.CanvasTexture;
  } {
    const holo = document.createElement("canvas");
    holo.width = 512;
    holo.height = 256;
    const ctx = holo.getContext("2d")!;
    const tex = new THREE.CanvasTexture(holo);
    tex.colorSpace = THREE.SRGBColorSpace;
    const mat = new THREE.MeshBasicMaterial({
      map: tex,
      transparent: true,
      opacity: 0.82,
      side: THREE.DoubleSide,
    });
    const left = new THREE.Mesh(new THREE.PlaneGeometry(7.4, 3.7), mat);
    const right = new THREE.Mesh(new THREE.PlaneGeometry(7.4, 3.7), mat);
    left.position.set(-10.5, 6.2, -16);
    right.position.set(10.5, 6.2, -16);
    left.rotation.y = 0.4;
    right.rotation.y = -0.4;
    this.root.add(left, right);
    return { left, right, ctx, tex };
  }

  private makeStars(): THREE.Points {
    const count = 700;
    const pos = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 160;
      pos[i * 3 + 1] = 8 + Math.random() * 60;
      pos[i * 3 + 2] = -20 - Math.random() * 120;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    return new THREE.Points(
      geo,
      new THREE.PointsMaterial({ color: 0xbdefff, size: 0.22, transparent: true, opacity: 0.85 }),
    );
  }

  private makeRain(): { points: THREE.Points; vel: Float32Array } {
    const count = 480;
    const pos = new Float32Array(count * 3);
    const vel = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 70;
      pos[i * 3 + 1] = Math.random() * 28;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 80;
      vel[i] = 10 + Math.random() * 18;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    const points = new THREE.Points(
      geo,
      new THREE.PointsMaterial({
        color: 0x7fefff,
        size: 0.08,
        transparent: true,
        opacity: 0.55,
        depthWrite: false,
      }),
    );
    return { points, vel };
  }

  private addSpinners(): void {
    for (let i = 0; i < 5; i++) {
      const body = new THREE.Group();
      const hull = new THREE.BoxGeometry(2.4, 0.28, 0.8);
      body.add(new THREE.Mesh(hull, new THREE.MeshBasicMaterial({ color: 0x080b12 })));
      body.add(neonEdges(hull, i % 2 ? 0x3df0ff : 0xffb347));
      const side = i % 2 === 0 ? -1 : 1;
      body.position.set(side * (18 + i * 2), 11 + (i % 3) * 1.4, -36 - i * 7);
      this.root.add(body);
      this.spinners.push(body);
    }
  }
}
